<script setup lang="ts">
	import '@/assets/carousel.css'
	import '@vuepic/vue-datepicker/dist/main.css'
	import { GoogleMap, Marker } from 'vue3-google-map'
	import VueEasyLightbox from 'vue-easy-lightbox'

	import { computed } from 'vue'
	import { useRoute } from 'vue-router'

	import ErrorBoundary from '@/components/ErrorBoundary.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import Modal from '@/components/ModalComponent.vue'
	import StyledAlert from '@/components/StyledAlert.vue'

	import AmenitySection from '@/modules/hotels/components/DetailsAmenitySection.vue'
	import AttractionSection from '@/modules/hotels/components/DetailsAttractionSection.vue'
	import ChangeDate from '@/modules/hotels/components/DetailsChangeDate.vue'
	import HotelDetailsMainSection from '@/modules/hotels/components/HotelDetailsMainSection.vue'
	import NoAvailabilityMessage from '@/modules/hotels/components/NoAvailabilityMessage.vue'
	import PhotosAndDetails from '@/modules/hotels/components/PhotosAndDetails.vue'
	import PolicyStatusBanner from '@/modules/hotels/components/PolicyStatusBanner.vue'
	import RoomList from '@/modules/hotels/components/RoomList.vue'
	import RoomsSort from '@/modules/hotels/components/RoomsSort.vue'
	import useHotelDetails from '@/modules/hotels/composables/useHotelDetails'
	import usePolicyEvaluator from '@/modules/hotels/composables/usePolicyEvaluator'
	import useHotelGroupPolicies from '@/modules/hotels/composables/useHotelGroupPolicies'
	import useUsers from '@/modules/users/composables/useUsers'

	import DetailsStickyBar from '../components/DetailsStickyBar.vue'
	import { amenityDisplay } from '../composables/useRoomAmenities.js'
	import { roomAmenityGroups } from '../utils/roomAmenities'

	const detailsRoute = useRoute()
	const { users } = useUsers()

	const detailsCompanyGroupId = computed(() => users.value[0]?.companyGroupId)

	const { policyObjectMap: detailsPolicyMap } = useHotelGroupPolicies({
		companyGroupId: detailsCompanyGroupId,
		immediate: true,
	})

	const detailsPolicyContext = computed(() => {
		const start = detailsRoute.query.Start as string | undefined
		const end = detailsRoute.query.End as string | undefined
		const nights = start && end
			? Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000)
			: undefined
		const daysInAdvance = start
			? Math.max(0, Math.ceil((new Date(start).getTime() - Date.now()) / 86_400_000))
			: undefined
		return { nights, daysInAdvance }
	})

	const { evaluations: detailsEvaluations } = usePolicyEvaluator(
		detailsPolicyMap,
		detailsPolicyContext,
	)

	const {
		rate,
		hotelDetails,
		photosAndDetails,
		hotel,
		hotelImages,
		haveAttractions,
		carouselModels,
		carouselImages,
		showMoreImages,
		hotelTexts,
		isLoading,
		isError,
		isFetching,
		message,
		attractions,
		generalAmenities,
		roomsList,
		selectedRoom,
		modals,
		openModalPaid,
		orderBy,
		expandRooms,
		mapView,
		imgs,
		index,
		visible,
		tabs,
		loadButton,
		form,
		mediaQueries,
		travelers,
		toggleMoreImages,
		toggleExpandRooms,
		openMap,
		onCopy,
		showMultiple,
		handleHide,
		orderByBeds,
	} = useHotelDetails()
	const selectedRoomAmenityGroups = computed(() =>
		roomAmenityGroups(selectedRoom.value?.amenities ?? [])
	)
</script>

<template>
	<div>
		<!-- Aísla el render del detalle: si una sección revienta (p. ej. data de un
		     proveedor con forma inesperada) muestra un fallback en vez de dejar la
		     página atascada en "Cargando..." -->
		<ErrorBoundary>
		<div v-if="hotel?.id && !isFetching">
			<DetailsStickyBar
				v-if="travelers.length"
				:tabs="tabs"
				:load-button="loadButton"
				:travelers="travelers"
			/>

			<!-- Main container -->
			<div class="mx-auto my-4 md:my-8 max-w-6xl px-3 sm:px-4">
				<div class="flex flex-col items-center justify-center gap-4 md:gap-6">
					<!-- Hotel details container -->
					<HotelDetailsMainSection
						ref="hotelDetails"
						:hotel="hotel"
						:hotel-images="hotelImages"
						:have-attractions="haveAttractions"
						:carousel-models="carouselModels"
						:message="message"
						@open-map="openMap"
						@on-copy="onCopy"
						@show-multiple="showMultiple"
					/>

					<!-- General amenities -->
					<AmenitySection
						:general-amenities="generalAmenities"
						:open-modal="modals.viewAllAmenities.open"
					/>

					<!-- Attractions -->
					<AttractionSection
						:have-attractions="haveAttractions"
						:attractions="attractions"
						:media-queries="mediaQueries"
					/>

					<!-- Change date -->
					<ChangeDate
						v-if="false"
						:load-button="loadButton"
						class-name="md:hidden flex"
						@handle-search="() => null"
					/>

					<!-- Policy status banner -->
					<PolicyStatusBanner
						v-if="detailsEvaluations.length"
						:evaluations="detailsEvaluations"
						variant="full"
						:show-compliant="true"
						class="w-full"
					/>

					<!-- Rooms -->
					<div
						v-if="roomsList.length"
						ref="rate"
						class="flex w-full flex-col gap-3 md:gap-5"
					>
						<!-- <DetailsCreditAlert
              :has-credit="hasCredit"
              v-model:open-modal-paid="openModalPaid"
            /> -->

						<RoomsSort
							v-model:order-by="orderBy"
							v-model:order-by-beds="orderByBeds"
							:rooms-list="roomsList"
							:show-sort="roomsList.length > 1 && expandRooms"
						/>

						<RoomList
							:expand-rooms="expandRooms"
							:rooms-list="roomsList"
							:hotel="hotel"
							:hotel-images="hotelImages"
							:modals="modals"
							@toggle-expand-rooms="toggleExpandRooms"
						/>
					</div>

					<!-- No rooms availability -->
					<div
						v-else
						ref="rate"
						class="w-full"
					>
						<StyledAlert
							alert="No hay habitaciones disponibles que cumplan con las políticas de su empresa."
							type="Info"
							:bordered="true"
							:hide-nav="true"
						/>
					</div>

					<PhotosAndDetails
						ref="photosAndDetails"
						:hotel="hotel"
						:hotel-images="hotelImages"
						:hotel-texts="hotelTexts"
						:have-attractions="haveAttractions"
						:show-more-images="showMoreImages"
						:carousel-images="carouselImages"
						@show-multiple="showMultiple"
						@toggle-more-images="toggleMoreImages"
						v-model:carousel-models="carouselModels"
					/>
				</div>
			</div>
		</div>

		<div v-else-if="isError && !isFetching">
			<NoAvailabilityMessage
				:form="form"
				class="mt-12 sm:mt-24 px-3 sm:px-0"
				:message="'No hemos encontrado habitaciones disponibles en el hotel seleccionado:'"
			>
				<template #help>
					<div
						class="text-sm sm:text-base md:text-[1.25rem] w-full font-medium text-left"
					>
						<p class="font-semibold mb-2">
							Si el problema persiste, le recomendamos:
						</p>

						<ul class="list-disc list-inside space-y-1 sm:space-y-2">
							<li>
								Enviar un correo a
								<a
									class="underline text-[#1F32DD] break-all"
									href="maito:reservaciones@enlaceforte.com"
									>reservaciones@enlaceforte.com</a
								>.
							</li>

							<li>
								Realizar una
								<RouterLink
									class="underline text-[#1F32DD]"
									:to="{ name: 'hotel.availability' }"
									>nueva búsqueda</RouterLink
								>.
							</li>
						</ul>
					</div>
				</template>
			</NoAvailabilityMessage>
		</div>

		<template #fallback>
			<div class="mx-auto my-16 max-w-lg px-4 text-center">
				<span class="material-symbols-outlined text-5xl text-gray-400">error</span>
				<h2 class="mt-4 text-xl font-medium text-gray-700">
					No pudimos mostrar el detalle de este hotel
				</h2>
				<p class="mt-2 text-gray-500">
					Ocurrió un error al presentar la información. Intenta con otro hotel
					o vuelve a la búsqueda.
				</p>
				<a
					href="/services/hotel/availability"
					class="mt-6 inline-block rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white"
				>
					Volver a la búsqueda
				</a>
			</div>
		</template>
		</ErrorBoundary>

		<!-- Full screen carousel -->
		<div>
			<VueEasyLightbox
				esc-enable
				:move-disabled="true"
				:visible="visible"
				:imgs="imgs"
				:index="index"
				@hide="handleHide"
			></VueEasyLightbox>
		</div>

		<!-- Map -->
		<div
			v-if="mapView"
			class="fixed top-0 bottom-0 left-0 right-0 z-50"
		>
			<div class="relative p-3 sm:p-4">
				<div
					class="relative z-10 flex items-center justify-center gap-2 sm:gap-3 text-white"
				>
					<p
						class="font-bold text-sm sm:text-base truncate max-w-[calc(100vw-80px)]"
					>
						{{ hotel.name }}
					</p>
					<button
						class="material-symbols-outlined cursor-pointer p-2 hover:bg-white/10 rounded-full transition-colors"
						@click="mapView = false"
					>
						close
					</button>
				</div>

				<div class="absolute inset-0 bg-blue-ef-400"></div>
			</div>

			<GoogleMap
				:map-type-control="false"
				:fullscreen-control="false"
				:street-view-control="false"
				api-key="AIzaSyDLM7Yvm2hjh8jzg62FVn1GzKm2RfFesxg"
				style="width: 100%; height: 100%"
				:center="{
					lat: parseFloat(hotel.latitude),
					lng: parseFloat(hotel.longitude),
				}"
				:zoom="15"
				:clickable-icons="false"
				:heading="320"
				:tilt="47.5"
			>
				<Marker
					:options="{
						position: {
							lat: parseFloat(hotel.latitude),
							lng: parseFloat(hotel.longitude),
						},
					}"
				/>
			</GoogleMap>
		</div>

		<!-- General amenities -->
		<Modal v-model="modals.viewAllAmenities.value">
			<template #modalBody>
				<section class="flex w-full max-w-3xl flex-col rounded-lg">
					<div class="flex flex-col gap-2 p-4 sm:p-6">
						<span class="text-base sm:text-lg font-medium"
							>Todas las amenidades generales:</span
						>

						<div
							class="grid max-h-[60vh] sm:max-h-96 gap-x-3 sm:gap-x-4 gap-y-2 overflow-y-auto md:grid-cols-2"
						>
							<template
								v-for="amenity in generalAmenities.amenities"
								:key="amenity"
							>
								<div class="flex items-center justify-start">
									<div class="flex min-w-0 flex-wrap items-center gap-2">
										<span class="material-symbols-outlined text-xl">
											{{ amenityDisplay(amenity).icon }}
										</span>
										<p class="min-w-0 break-words text-sm">
											{{ amenityDisplay(amenity).label }}
										</p>
										<span v-if="generalAmenities.paidAmenities.has(amenity)" class="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">Con cargo</span>
									</div>
								</div>
							</template>
						</div>
					</div>

					<div class="sm:grid sm:grid-flow-row-dense sm:grid-cols-1 sm:gap-0">
						<button
							type="button"
							class="close-modal-button"
							@click="modals.viewAllAmenities.close"
						>
							Cerrar
						</button>
					</div>
				</section>
			</template>
		</Modal>

		<!-- Selected room info -->
		<Modal v-model="modals.viewMoreDetails.value">
			<template #modalBody>
				<section class="flex w-full max-w-3xl flex-col rounded-lg">
					<div class="flex flex-col gap-2 p-4 sm:p-6">
						<span class="text-base sm:text-lg font-medium"
							>Amenidades de la habitación:
						</span>

						<div v-if="selectedRoomAmenityGroups.length" class="max-h-[60vh] sm:max-h-96 space-y-4 overflow-y-auto pr-1">
							<section v-for="group in selectedRoomAmenityGroups" :key="group.name">
								<h3 class="mb-2 text-sm font-semibold text-gray-800">{{ group.name }}</h3>
								<div class="grid gap-x-3 gap-y-2 md:grid-cols-2">
									<div v-for="amenity in group.items" :key="amenity.code" class="flex min-w-0 items-start gap-2 text-xs sm:text-sm">
										<span class="material-symbols-outlined shrink-0 text-lg" aria-hidden="true">{{ amenity.icon }}</span>
										<span class="break-words">{{ amenity.label }}</span>
									</div>
								</div>
							</section>
						</div>
					</div>

					<div class="sm:grid sm:grid-flow-row-dense sm:grid-cols-1 sm:gap-0">
						<button
							type="button"
							class="close-modal-button"
							@click="modals.viewMoreDetails.close"
						>
							Cerrar
						</button>
					</div>
				</section>
			</template>
		</Modal>

		<!-- Credit info -->
		<Modal v-model="openModalPaid">
			<template #modalBody>
				<div class="flex flex-col gap-4 sm:gap-6 p-4 sm:p-6">
					<div class="flex gap-3 items-start">
						<span class="material-symbols-outlined text-gray-700 flex-shrink-0">
							done
						</span>
						<p class="text-sm sm:text-base">Sin cargo hoy.</p>
					</div>
					<div class="flex gap-3 items-start">
						<span class="material-symbols-outlined text-gray-700 flex-shrink-0">
							today
						</span>
						<p class="text-sm sm:text-base">
							Cobrado a su empresa el día después del check-in.
						</p>
					</div>
					<div class="flex gap-3 items-start">
						<span class="material-symbols-outlined text-gray-700 flex-shrink-0">
							receipt_long
						</span>
						<p class="text-sm sm:text-base">
							Recibo proporcionado por Enlacebooking (excluye imprevistos).
						</p>
					</div>
				</div>
				<div class="sm:grid sm:grid-flow-row-dense sm:grid-cols-1 sm:gap-0">
					<button
						type="button"
						class="close-modal-button"
						@click="openModalPaid = false"
					>
						Enterado
					</button>
				</div>
			</template>
		</Modal>
	</div>

	<LoadingModal :when="isLoading || isFetching" />
</template>

<style>
	/* Transiciones de lista */
	.room-list-move,
	.room-list-enter-active,
	.room-list-leave-active {
		transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.room-list-enter-from,
	.room-list-leave-to {
		opacity: 0;
		transform: translateY(20px) scale(0.95);
	}

	/* Componentes base */
	.order-by {
		@apply !bg-white text-sm text-black;
	}

	/* Carrusel premium */
	.carousel__item {
		width: 100%;
		height: 100%;
		background-color: var(--vc-clr-primary);
		color: var(--vc-clr-white);
		font-size: 20px;
		border-radius: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;
	}

	.carousel__slide {
		padding: 0;
	}

	.carousel__icon {
		color: #1f2937;
		font-size: 2rem;
	}

	.carousel__prev,
	.carousel__next {
		box-sizing: content-box;
		border: none;
		background: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(8px);
		box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
		border-radius: 50%;
		width: 48px;
		height: 48px;
		transition: all 0.3s ease;
	}

	.carousel__prev:hover,
	.carousel__next:hover {
		background: white;
		transform: scale(1.1);
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15);
	}

	#content {
		height: 200px;
		overflow: auto;
		border: 1px solid red;
		padding: 0 10px;
	}

	.disclosure-button {
		@apply flex w-full items-center justify-between rounded-xl border-2 bg-gray-50 p-3 text-left font-semibold text-gray-700 transition-all duration-300 hover:border-gray-300 hover:bg-gray-100;
	}

	/* Estrellas */
	.star {
		@apply h-5 w-5 text-yellow-400;
	}
	.star--remain {
		@apply !text-gray-300;
	}
	.star--xl {
		@apply !h-6 !w-6;
	}

	/* Mapa */
	.map-link {
		background-image: url('@/assets/imgs/ilustrative_map.png');
		background-color: rgba(255, 255, 255, 1);
		background-position: center;
		background-size: cover;
		justify-content: center;
		align-items: center;
		border: 1px solid rgb(167, 167, 167);
	}

	.close-modal-button {
		@apply inline-flex min-h-[44px] w-full justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-blue-ef-100 shadow-sm ring-1 ring-inset ring-gray-300 transition-all duration-200 hover:bg-gray-50 hover:ring-gray-400 active:bg-gray-100 sm:col-start-1 sm:mt-0 sm:py-3 sm:text-base;
	}

	.map-handler:hover .map-button {
		@apply !bg-white;
	}

	.map-handler:hover .map-ilustrative {
		@apply !opacity-10;
	}

	/* Animaciones personalizadas */
	@keyframes shimmer {
		0% {
			background-position: -200% center;
		}
		100% {
			background-position: 200% center;
		}
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	/* Scroll suave para las secciones */
	html {
		scroll-behavior: smooth;
	}
</style>
